// ✅ Trekkin.app — useShareStore.jsx v3.0.3 (LIVE PRO + SAFARI-SAFE)
// 🔹 Base = v3.0.0 (NADA foi removido, só reforçado)
// 🔹 Estrutura Firebase:
//    liveShares/{shareId}/
//      - user { uid, name, email }
//      - active: bool
//      - startTime, endTime
//      - position { lat, lon, ele, spd, acc, ts }
//      - stats { distanceKm, movingTime, stoppedTime, ... }
//      - path/{autoId} { lat, lon, ele, ts }
//      - lastUpdate
// 🔹 Guia (app):
//      startShare() → cria nó
//      updateShareData({ lat, lon, ele, acc, spd, ts, stats, pathPoint })
//      stopShare() → active=false + endTime
// 🔹 Viewer (link):
//      listenShare(shareId, cb?) → onValue em tempo real
//      payload = { id, active, user, position, stats, path[], lastUpdate }
// 🔹 v3.0.3: SAFE PATCH
//      - path sempre array ordenado (mesmo com snapshots parciais)
//      - filtra pontos inválidos (lat/lon NaN)
//      - lastUpdate com fallbacks seguros (Safari / ts undefined)
//      - callback continua opcional e 100% compatível

import { create } from "zustand";
import { db, auth } from "../firebaseConfig";
import {
  ref,
  set as fbSet,
  onValue,
  onChildAdded,
  onChildChanged,
  onChildRemoved,
  push,
  get as fbGet,
  update,
  query,
  orderByChild,
  equalTo,
  limitToLast,
} from "firebase/database";
import { translateNow } from "../i18n/translateNow";
import { Capacitor } from "@capacitor/core";
import { usePanelsStore } from "./usePanelsStore";

const LIVE_SHARE_ID_KEY = "liveShareId";
const LIVE_SHARE_LINK_KEY = "liveShareLink";
const LIVE_SHARE_SYNC_MIN_MS = 4000;
const LIVE_SHARE_VIEWER_MAX_PATH_POINTS = 1200;

const makeShareLink = (shareId) =>
  shareId ? `https://trekkin.pt/app/?share=${shareId}` : "";

const parseShareIdFromLink = (link) => {
  if (!link || typeof link !== "string") return null;
  try {
    const url = new URL(link);
    const id = url.searchParams.get("share");
    return id || null;
  } catch {
    const match = link.match(/[?&]share=([^&]+)/i);
    return match?.[1] || null;
  }
};

const readLocalShareId = () => {
  try {
    const direct = localStorage.getItem(LIVE_SHARE_ID_KEY);
    if (direct) return direct;
    const link = localStorage.getItem(LIVE_SHARE_LINK_KEY);
    return parseShareIdFromLink(link);
  } catch {
    return null;
  }
};

const persistLocalShare = (shareId) => {
  if (!shareId) return;
  try {
    localStorage.setItem(LIVE_SHARE_ID_KEY, shareId);
    localStorage.setItem(LIVE_SHARE_LINK_KEY, makeShareLink(shareId));
  } catch {}
};

const clearLocalShare = () => {
  try {
    localStorage.removeItem(LIVE_SHARE_ID_KEY);
    localStorage.removeItem(LIVE_SHARE_LINK_KEY);
  } catch {}
};

function getAndroidGPSPlugin() {
  try {
    if (!Capacitor?.isNativePlatform?.()) return null;
    if (Capacitor.getPlatform() !== "android") return null;
    return (
      window?.Capacitor?.Plugins?.GPSNativePlugin ||
      window?.Capacitor?.Plugins?.GPSNative ||
      Capacitor?.Plugins?.GPSNativePlugin ||
      Capacitor?.Plugins?.GPSNative ||
      null
    );
  } catch {
    return null;
  }
}

let shareSyncInFlight = null;
let lastShareSyncAt = 0;

async function fetchSharesByUser(uid) {
  if (!uid) return [];

  const normalize = (snap) => {
    if (!snap?.exists?.()) return [];
    const raw = snap.val() || {};
    return Object.entries(raw)
      .map(([id, val]) => ({ id, ...(val || {}) }))
      .filter((item) => !!item?.id);
  };

  try {
    const snap = await fbGet(
      query(ref(db, "liveShares"), orderByChild("user/uid"), equalTo(uid))
    );
    return normalize(snap);
  } catch (err) {
    console.warn("[LIVE SHARE] query por user falhou", err);
    return [];
  }
}

async function fetchShareById(shareId) {
  if (!shareId) return null;
  try {
    const snap = await fbGet(ref(db, `liveShares/${shareId}`));
    if (!snap.exists()) return null;
    return { id: shareId, ...(snap.val() || {}) };
  } catch (err) {
    console.warn("[LIVE SHARE] leitura por id falhou", err);
    return null;
  }
}

export const useShareStore = create((set, get) => ({
  // Estado base (guia)
  isSharing: false,
  shareId: null,
  isStartingShare: false,

  // Viewer
  liveData: null,   // posição atual
  liveStats: null,  // estatísticas agregadas
  livePath: [],     // rasto completo (ordenado por ts)
  liveMeta: null,   // { active, user, lastUpdate }

  // --------------------------------------------------
  // 🔄 Sync estado com Firebase (1 ativo por user)
  // --------------------------------------------------
  async syncShareState({ reason = "boot", force = false } = {}) {
    const now = Date.now();
    if (!force && shareSyncInFlight) return shareSyncInFlight;
    if (!force && now - lastShareSyncAt < LIVE_SHARE_SYNC_MIN_MS) return null;

    const firebaseUser = auth?.currentUser || null;
    const uid = firebaseUser?.uid || null;
    if (!uid) return null;

    const localId = get().shareId || readLocalShareId();

    shareSyncInFlight = (async () => {
      let shares = await fetchSharesByUser(uid);

      if (localId && !shares.find((s) => s.id === localId)) {
        const byId = await fetchShareById(localId);
        if (byId && byId.user?.uid === uid) {
          shares = [...shares, byId];
        }
      }

      const activeShares = shares.filter((s) => s?.active === true);

      let chosen = null;
      if (localId) {
        chosen = activeShares.find((s) => s.id === localId) || null;
      }

      if (!chosen && activeShares.length) {
        chosen = activeShares.reduce((best, cur) => {
          const bestTs =
            Number(best?.lastUpdate) ||
            Number(best?.startTime) ||
            0;
          const curTs =
            Number(cur?.lastUpdate) ||
            Number(cur?.startTime) ||
            0;
          return curTs > bestTs ? cur : best;
        }, activeShares[0]);
      }

      if (activeShares.length > 1 && chosen?.id) {
        const nowTs = Date.now();
        const updates = {};
        activeShares.forEach((s) => {
          if (s.id === chosen.id) return;
          updates[`liveShares/${s.id}/active`] = false;
          updates[`liveShares/${s.id}/endTime`] = nowTs;
          updates[`liveShares/${s.id}/lastUpdate`] = nowTs;
        });
        if (Object.keys(updates).length) {
          await update(ref(db), updates);
        }
      }

      if (chosen?.id) {
        set({ isSharing: true, shareId: chosen.id });
        persistLocalShare(chosen.id);
        return chosen.id;
      }

      clearLocalShare();
      if (get().isSharing || get().shareId) {
        set({ isSharing: false, shareId: null });
      }
      return null;
    })();

    try {
      return await shareSyncInFlight;
    } finally {
      lastShareSyncAt = Date.now();
      shareSyncInFlight = null;
      console.log("🔁 LIVE Share sync concluído", { reason });
    }
  },

  // --------------------------------------------------
  // 🚀 Inicia nova partilha (guia)
  // --------------------------------------------------
  async startShare() {
    const { isSharing, shareId, isStartingShare } = get();
    if (isStartingShare) return null;
    if (isSharing && shareId) {
      return `https://trekkin.pt/app/?share=${shareId}`;
    }
    if (!usePanelsStore.getState()?.isRecording) {
      console.warn("⚠️ startShare ignorado sem gravação ativa.");
      return null;
    }
    const user = auth.currentUser;
    if (!user) {
      alert(translateNow("share.alert.authRequired"));
      return null;
    }

    set({ isStartingShare: true });
    try {
      const resumed = await get().syncShareState({
        reason: "startShare",
        force: true,
      });
      const current = get();
      if (resumed && current.isSharing && current.shareId) {
        return makeShareLink(current.shareId);
      }

      const newRef = push(ref(db, "liveShares"));
      const shareId = newRef.key;

      const now = Date.now();

      const payload = {
        user: {
          uid: user.uid,
          name: user.displayName || user.email,
          email: user.email,
        },
        startTime: now,
        active: true,
        position: {},
        stats: {},
        lastUpdate: now,
      };

      await fbSet(ref(db, `liveShares/${shareId}`), payload);

      const link = `https://trekkin.pt/app/?share=${shareId}`;

      set({ isSharing: true, shareId });
      persistLocalShare(shareId);

      // Android: reforçar targets no nativo para envio em BG (Firebase REST)
      try {
        const plugin = getAndroidGPSPlugin();
        if (plugin?.setLiveTargets) {
          const token = user.getIdToken ? await user.getIdToken(true) : null;
          if (token) {
            plugin.setLiveTargets({
              enabled: true,
              uid: user.uid,
              shareId,
              token,
              name: user.displayName || user.email || null,
              apiKey: "AIzaSyBAw86VN77OgmU_1g2lTITz0yfI8QDh6k4",
              refreshToken:
                typeof user?.stsTokenManager?.refreshToken === "string"
                  ? user.stsTokenManager.refreshToken
                  : typeof user?.refreshToken === "string"
                  ? user.refreshToken
                  : null,
            });
            plugin.setBackgroundDispatch?.({ enabled: true });
            console.log("[ANDROID] setLiveTargets (share) →", shareId);
          }
        }
      } catch (e) {
        console.warn("[ANDROID] setLiveTargets (share) falhou:", e);
      }

      console.log("🔗 LIVE Share iniciado:", shareId, link);
      return link;
    } finally {
      set({ isStartingShare: false });
    }
  },

  // --------------------------------------------------
  // 🛑 Termina partilha ativa (guia)
  // ↪️ Em vez de apagar o nó, marca active=false
  //     → Viewer continua a ver o rasto e última posição
  // --------------------------------------------------
  async stopShare() {
    const { shareId, isSharing } = get();
    if (!isSharing || !shareId) {
      console.warn("⚠️ stopShare chamado sem partilha ativa.");
      return;
    }

    try {
      const endTime = Date.now();
      await fbSet(ref(db, `liveShares/${shareId}/active`), false);
      await fbSet(ref(db, `liveShares/${shareId}/endTime`), endTime);
      await fbSet(ref(db, `liveShares/${shareId}/lastUpdate`), endTime);
      console.log("🛑 LIVE Share marcado como encerrado:", shareId);
    } catch (err) {
      console.warn("⚠️ Erro ao terminar partilha:", err);
    } finally {
      // Android: limpar targets no nativo
      try {
        const plugin = getAndroidGPSPlugin();
        plugin?.setLiveTargets?.({ enabled: false });
        plugin?.setBackgroundDispatch?.({ enabled: false });
      } catch {}
      clearLocalShare();
      set({ isSharing: false, shareId: null, isStartingShare: false });
    }
  },

  // --------------------------------------------------
  // 📡 Atualiza dados GPS (guia)
  // data = {
  //   lat, lon, ele?, acc?, spd?, ts?,
  //   stats?,        // vindo de usePanelsStore.getMetrics()
  //   pathPoint?     // { lat, lon, ele, ts } último ponto do rasto
  // }
  // --------------------------------------------------
  async updateShareData(data) {
    const { shareId, isSharing } = get();
    if (!isSharing || !shareId) return;

    if (
      data == null ||
      typeof data.lat !== "number" ||
      typeof data.lon !== "number"
    ) {
      return;
    }

    const now = Date.now();
    const ts = typeof data.ts === "number" ? data.ts : now;

    const pos = {
      lat: data.lat,
      lon: data.lon,
      ele: typeof data.ele === "number" ? data.ele : 0,
      spd: typeof data.spd === "number" ? data.spd : 0,
      acc: typeof data.acc === "number" ? data.acc : 0,
      ts,
    };

    try {
      // Posição atual
      await fbSet(ref(db, `liveShares/${shareId}/position`), pos);
      await fbSet(ref(db, `liveShares/${shareId}/lastUpdate`), ts);

      // Estatísticas (se fornecidas)
      if (data.stats && typeof data.stats === "object") {
        
        const s = data.stats;

        // 🔄 MAPA UNIVERSAL DE CAMPOS
        const fixedStats = {
          // tempo total
          totalTime: s.totalTime ?? s.elapsed ?? 0,

          // tempo a andar
          movingTime: s.movingTime ?? s.moving ?? 0,

          // tempo parado
          stoppedTime: s.stoppedTime ?? s.pause ?? 0,

          // distância em km (normalize)
          distanceKm:
            s.distanceKm ??
            (typeof s.distance === "number" ? s.distance / 1000 : 0),

          // velocidade atual (km/h)
          currentSpeed: s.currentSpeed ?? s.speed ?? 0,

          // desnível +
          elevationGain: s.elevationGain ?? s.dPlus ?? 0,

          // pontos do track
          points: s.points ?? 0,
        };

        await fbSet(ref(db, `liveShares/${shareId}/stats`), fixedStats);
      }

      // Ponto de rasto (se fornecido)
      if (data.pathPoint && typeof data.pathPoint.lat === "number") {
        const pp = {
          lat: data.pathPoint.lat,
          lon: data.pathPoint.lon,
          ele:
            typeof data.pathPoint.ele === "number"
              ? data.pathPoint.ele
              : pos.ele,
          ts: data.pathPoint.ts || ts,
        };
        await push(ref(db, `liveShares/${shareId}/path`), pp);
      }
    } catch (err) {
      console.warn("⚠️ Erro ao atualizar partilha:", err);
    }
  },

  // --------------------------------------------------
  // 👀 Viewer (convidado) — escuta TUDO
  // listenShare(shareId, cb?) → devolve função unsubscribe
  // cb(payload|null)
  //    payload = {
  //      id, active, user, position, stats, path[], lastUpdate
  //    }
  // --------------------------------------------------
  listenShare(shareId, cb) {
    if (!shareId) return () => {};

    const basePath = `liveShares/${shareId}`;
    const activeRef = ref(db, `${basePath}/active`);
    const userRef = ref(db, `${basePath}/user`);
    const positionRef = ref(db, `${basePath}/position`);
    const statsRef = ref(db, `${basePath}/stats`);
    const lastUpdateRef = ref(db, `${basePath}/lastUpdate`);
    const startTimeRef = ref(db, `${basePath}/startTime`);
    const endTimeRef = ref(db, `${basePath}/endTime`);
    const pathRef = query(
      ref(db, `${basePath}/path`),
      orderByChild("ts"),
      limitToLast(LIVE_SHARE_VIEWER_MAX_PATH_POINTS)
    );

    const state = {
      active: true,
      user: null,
      position: null,
      stats: null,
      lastUpdate: null,
      startTime: null,
      endTime: null,
      pathMap: new Map(),
      path: [],
      missing: false,
    };

    const emitNull = () => {
      set({
        liveData: null,
        liveStats: null,
        livePath: [],
        liveMeta: null,
      });
      cb?.(null);
    };

    const rebuildPath = () => {
      state.path = Array.from(state.pathMap.values())
        .filter(
          (p) =>
            p &&
            typeof p.lat === "number" &&
            typeof p.lon === "number" &&
            !Number.isNaN(p.lat) &&
            !Number.isNaN(p.lon)
        )
        .sort((a, b) => (a.ts || 0) - (b.ts || 0));
    };

    const emit = () => {
      if (state.missing) {
        emitNull();
        return;
      }

      const lastUpdate =
        state.lastUpdate ||
        (state.position && state.position.ts) ||
        state.endTime ||
        state.startTime ||
        Date.now();

      const payload = {
        id: shareId,
        active: !!state.active,
        user: state.user || null,
        position: state.position || null,
        stats: state.stats || null,
        path: state.path,
        lastUpdate,
      };

      set({
        liveData: payload.position,
        liveStats: payload.stats,
        livePath: payload.path,
        liveMeta: {
          active: payload.active,
          user: payload.user,
          lastUpdate: payload.lastUpdate,
        },
      });

      cb?.(payload);
    };

    const upsertPathPoint = (key, value) => {
      if (!key) return;
      if (
        !value ||
        typeof value.lat !== "number" ||
        typeof value.lon !== "number" ||
        Number.isNaN(value.lat) ||
        Number.isNaN(value.lon)
      ) {
        state.pathMap.delete(key);
      } else {
        state.pathMap.set(key, value);
      }
      rebuildPath();
    };

    // Check leve para evitar fallback de nó inexistente sem descarregar path inteiro.
    fbGet(activeRef)
      .then((snap) => {
        if (!snap.exists()) {
          state.missing = true;
          console.log("👀 Viewer: partilha não encontrada:", shareId);
          emitNull();
        }
      })
      .catch(() => {});

    const unsubscribers = [];

    unsubscribers.push(
      onValue(activeRef, (snap) => {
        if (!snap.exists()) return;
        state.active = !!snap.val();
        emit();
      })
    );

    unsubscribers.push(
      onValue(userRef, (snap) => {
        state.user = snap.exists() ? snap.val() : null;
        emit();
      })
    );

    unsubscribers.push(
      onValue(positionRef, (snap) => {
        state.position = snap.exists() ? snap.val() : null;
        emit();
      })
    );

    unsubscribers.push(
      onValue(statsRef, (snap) => {
        state.stats = snap.exists() ? snap.val() : null;
        emit();
      })
    );

    unsubscribers.push(
      onValue(lastUpdateRef, (snap) => {
        state.lastUpdate = snap.exists() ? snap.val() : null;
        emit();
      })
    );

    unsubscribers.push(
      onValue(startTimeRef, (snap) => {
        state.startTime = snap.exists() ? snap.val() : null;
        emit();
      })
    );

    unsubscribers.push(
      onValue(endTimeRef, (snap) => {
        state.endTime = snap.exists() ? snap.val() : null;
        emit();
      })
    );

    unsubscribers.push(
      onChildAdded(pathRef, (snap) => {
        upsertPathPoint(snap.key, snap.val());
        emit();
      })
    );

    unsubscribers.push(
      onChildChanged(pathRef, (snap) => {
        upsertPathPoint(snap.key, snap.val());
        emit();
      })
    );

    unsubscribers.push(
      onChildRemoved(pathRef, (snap) => {
        state.pathMap.delete(snap.key);
        rebuildPath();
        emit();
      })
    );

    return () => {
      unsubscribers.forEach((unsub) => {
        try {
          unsub?.();
        } catch {}
      });
    };
  },
}));

console.log(
  "✅ useShareStore inicializado →",
  Object.keys(useShareStore.getState())
);
